import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import StarRoundedIcon from '@material-ui/icons/StarRounded';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import NearMeOutlinedIcon from '@material-ui/icons/NearMeOutlined';
import { ErrorLable } from './MultiCheckbox';
import { FranchiseType } from '../../../../../../api/franchise';

type LatLng = { lat: number; lng: number };

type Props = {
  franchises: FranchiseType[];
  selectedId: number | null;
  onSelect: (franchise: FranchiseType) => void;
  projectState: string | undefined;
  projectLatLng?: LatLng | null;
  errors: any;
};

const NAME = 'franchise';

const formatLocation = (fr: FranchiseType): string => {
  const parts = [fr.city, fr.state].filter(Boolean);
  return parts.join(', ');
};

const formatContact = (fr: FranchiseType): string => {
  if (fr.contactPerson) return fr.contactPerson;
  return [fr.firstName, fr.lastName].filter(Boolean).join(' ');
};

const buildFranchiseAddress = (fr: FranchiseType): string =>
  [fr.street, fr.city, fr.state, fr.zipCode]
    .filter(Boolean)
    .join(', ') || (fr.address || '');

const haversineMiles = (a: LatLng, b: LatLng): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 3958.7613; // Earth radius in miles
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};

const formatDistance = (miles: number): string => {
  if (miles < 0.1) return '<0.1 mi away';
  if (miles < 10) return `${miles.toFixed(1)} mi away`;
  return `${Math.round(miles)} mi away`;
};

const OfficeCards: FC<Props> = ({
  franchises,
  selectedId,
  onSelect,
  projectState,
  projectLatLng,
  errors,
}) => {
  const hasError = errors[NAME] !== undefined;
  const userPickedRef = useRef<boolean>(false);
  const [coords, setCoords] = useState<Record<number, LatLng | null>>({});

  useEffect(() => {
    if (!projectLatLng) return undefined;
    let cancelled = false;
    franchises.forEach(fr => {
      if (fr.id == null) return;
      if (coords[fr.id] !== undefined) return;
      const addr = buildFranchiseAddress(fr);
      if (!addr) {
        setCoords(prev => ({ ...prev, [fr.id as number]: null }));
        return;
      }
      geocodeByAddress(addr)
        .then(results => getLatLng(results[0]))
        .then(latLng => {
          if (cancelled) return;
          setCoords(prev => ({ ...prev, [fr.id as number]: latLng }));
        })
        .catch(() => {
          if (cancelled) return;
          setCoords(prev => ({ ...prev, [fr.id as number]: null }));
        });
    });
    return () => {
      cancelled = true;
    };
  }, [franchises, projectLatLng]);

  const decorated = useMemo(
    () =>
      franchises.map(fr => {
        const c = fr.id != null ? coords[fr.id] : null;
        const distance =
          projectLatLng && c ? haversineMiles(projectLatLng, c) : null;
        return { fr, distance };
      }),
    [franchises, coords, projectLatLng],
  );

  const sorted = useMemo(() => {
    if (!projectLatLng) return decorated;
    const list = [...decorated];
    list.sort((a, b) => {
      if (a.distance == null && b.distance == null) return 0;
      if (a.distance == null) return 1;
      if (b.distance == null) return -1;
      return a.distance - b.distance;
    });
    return list;
  }, [decorated, projectLatLng]);

  const badgeId = useMemo(() => {
    if (!projectLatLng) return null;
    const closest = sorted.find(x => x.distance != null);
    return closest?.fr.id ?? null;
  }, [sorted, projectLatLng]);

  const recommendedId = useMemo(() => {
    if (badgeId != null) return badgeId;
    if (projectState) {
      const stateMatch = franchises.find(
        f => (f.state || '').toUpperCase() === projectState.toUpperCase(),
      );
      if (stateMatch?.id != null) return stateMatch.id;
    }
    const naples = franchises.find(
      f => (f.name || '').trim().toLowerCase() === 'naples',
    );
    return naples?.id ?? null;
  }, [badgeId, projectState, franchises]);

  useEffect(() => {
    if (userPickedRef.current) return;
    if (selectedId) return;
    if (recommendedId == null) return;
    const fr = franchises.find(f => f.id === recommendedId);
    if (fr) onSelect(fr);
  }, [recommendedId, franchises, selectedId]);

  const handleSelect = (fr: FranchiseType) => {
    userPickedRef.current = true;
    onSelect(fr);
  };

  if (franchises.length === 0) {
    return <EmptyState>Loading offices…</EmptyState>;
  }

  return (
    <Wrapper>
      {hasError && <ErrorLable>{errors[NAME].message}</ErrorLable>}
      <CardsList role="radiogroup" aria-label="Processing office">
        {sorted.map(({ fr, distance }) => {
          const checked = selectedId === fr.id;
          const recommended = badgeId != null && fr.id === badgeId;
          const location = formatLocation(fr);
          const contact = formatContact(fr);
          return (
            <Card
              key={fr.id}
              type="button"
              role="radio"
              aria-checked={checked}
              onClick={() => handleSelect(fr)}
              $checked={checked}
              $error={hasError && !checked}
            >
              <CardHead>
                <NameRow>
                  <Name>{fr.name}</Name>
                  {recommended && (
                    <RecommendedBadge>
                      <StarRoundedIcon style={{ fontSize: 12 }} />
                      Recommended for your area
                    </RecommendedBadge>
                  )}
                </NameRow>
                <CheckIndicator $checked={checked} aria-hidden>
                  {checked && <CheckRoundedIcon style={{ fontSize: 14 }} />}
                </CheckIndicator>
              </CardHead>
              {distance != null && (
                <DistanceRow $highlight={recommended}>
                  <NearMeOutlinedIcon style={{ fontSize: 14 }} />
                  {formatDistance(distance)}
                </DistanceRow>
              )}
              <MetaRow>
                {location && (
                  <MetaItem>
                    <RoomOutlinedIcon style={{ fontSize: 15 }} />
                    {location}
                  </MetaItem>
                )}
                {fr.phoneNumber && (
                  <MetaItem>
                    <PhoneOutlinedIcon style={{ fontSize: 15 }} />
                    {fr.phoneNumber}
                  </MetaItem>
                )}
                {contact && (
                  <MetaItem>
                    <PersonOutlineIcon style={{ fontSize: 15 }} />
                    {contact}
                  </MetaItem>
                )}
              </MetaRow>
            </Card>
          );
        })}
      </CardsList>
    </Wrapper>
  );
};

export default OfficeCards;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyState = styled.div`
  padding: 16px;
  border: 1px dashed var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  font-size: 0.8125rem;
  color: var(--neutral-500);
  text-align: center;
`;

const CardsList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media only screen and (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.button<{ $checked: boolean; $error: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  padding: 14px 16px;
  background: ${({ $checked }) => ($checked ? 'var(--primary-50)' : 'var(--neutral-0)')};
  border: 1.5px solid
    ${({ $checked, $error }) => {
    if ($checked) return 'var(--primary-600)';
    if ($error) return 'var(--danger-500, #dc2626)';
    return 'var(--border)';
  }};
  border-radius: 12px;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ $checked }) => ($checked ? 'var(--primary-700, #0b5a87)' : 'var(--neutral-400)')};
    background: ${({ $checked }) => ($checked ? 'var(--primary-50)' : 'var(--neutral-50)')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
`;

const Name = styled.span`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.3;
`;

const RecommendedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 20px;
  padding: 0 8px;
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
  border-radius: 999px;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;

  svg {
    color: #059669;
  }
`;

const CheckIndicator = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1.5px solid ${({ $checked }) => ($checked ? 'var(--primary-600)' : 'var(--neutral-300)')};
  background: ${({ $checked }) => ($checked ? 'var(--primary-600)' : 'var(--neutral-0)')};
  color: var(--neutral-0);
  margin-top: 2px;
  transition: background-color 0.15s ease, border-color 0.15s ease;
`;

const DistanceRow = styled.span<{ $highlight?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ $highlight }) =>
    $highlight ? '#047857' : 'var(--neutral-600)'};

  svg {
    color: ${({ $highlight }) =>
    $highlight ? '#059669' : 'var(--neutral-500)'};
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: var(--neutral-600);
  min-width: 0;

  svg {
    color: var(--neutral-500);
    flex-shrink: 0;
  }
`;
